import {
  type Point2,
  type ReadableStore,
  type Selection,
  createStore,
} from '@frontend/viewer-core';

import type { RoleChromeManager, RoleChromeState, ViewerMode } from './types';

type RoleChromeInternal = {
  anchor: Point2 | null;
  holdHidden: boolean;
};

const CLOSED: RoleChromeInternal = {
  anchor: null,
  holdHidden: false,
};

const HIDDEN: RoleChromeState = { kind: 'hidden' };
const FLYOUT: RoleChromeState = { kind: 'flyout' };

const chromeEqual = (a: RoleChromeState, b: RoleChromeState): boolean => {
  if (a.kind !== b.kind) {
    return false;
  }
  return a.kind !== 'context' || b.kind !== 'context' || a.anchor === b.anchor;
};

/**
 * Exclusive mapping-role chrome: flyout, context menu, or neither.
 *
 * The two React menus only read `kind`. Writers call `open` / `dismiss` /
 * `close` / `release` and never name the other menu. Empty selection and
 * leaving mapping mode derive `hidden` on their own — the same reason
 * `createMappingSnapshotSlice` exists: two independent writes would leave a
 * frame that should never have been on screen.
 *
 * `holdHidden` is the pointerdown/pointerup gap. Dismissing the context menu
 * on press must not fall through to the flyout before canvas selection
 * updates on release. That hold lives here, not as one menu suppressing the
 * other.
 */
export const createRoleChromeController = ({
  mode,
  selection,
}: {
  mode: ReadableStore<ViewerMode>;
  selection: ReadableStore<Selection>;
}): RoleChromeManager => {
  const store = createStore(CLOSED);
  let cached: RoleChromeState = HIDDEN;

  const derive = (): RoleChromeState => {
    if (mode.getState() !== 'mapping' || selection.getState().size === 0) {
      return HIDDEN;
    }
    const { anchor, holdHidden } = store.getState();
    if (anchor) {
      return { kind: 'context', anchor };
    }
    if (holdHidden) {
      return HIDDEN;
    }
    return FLYOUT;
  };

  const getState = (): RoleChromeState => {
    const next = derive();
    if (chromeEqual(cached, next)) {
      return cached;
    }
    cached =
      next.kind === 'context' ? next : next.kind === 'flyout' ? FLYOUT : HIDDEN;
    return cached;
  };

  selection.subscribe(() => {
    if (selection.getState().size > 0) {
      return;
    }
    store.setState((previous) =>
      previous.anchor === null && !previous.holdHidden ? previous : CLOSED,
    );
  });

  return {
    getState,
    subscribe(listener) {
      let previous = getState();
      const notify = () => {
        const next = getState();
        if (Object.is(next, previous)) {
          return;
        }
        previous = next;
        listener();
      };
      const unsubs = [
        store.subscribe(notify),
        mode.subscribe(notify),
        selection.subscribe(notify),
      ];
      return () => {
        unsubs.forEach((unsubscribe) => unsubscribe());
      };
    },
    open(screenPoint) {
      store.setState(() => ({
        anchor: screenPoint,
        holdHidden: false,
      }));
    },
    dismiss() {
      store.setState((previous) => {
        if (previous.anchor === null) {
          return previous;
        }
        return { anchor: null, holdHidden: true };
      });
    },
    close() {
      store.setState((previous) =>
        previous.anchor === null && !previous.holdHidden ? previous : CLOSED,
      );
    },
    release() {
      store.setState((previous) =>
        previous.holdHidden ? { ...previous, holdHidden: false } : previous,
      );
    },
  };
};
